/**
 * Cartesian BI charts (D529): `Histogram` (+ cumulative curve and markers), `ComboChart` (bars on one axis, lines on a
 * second, with the gridlines aligned), `ParetoChart` (sorted bars, cumulative line, ABC classes), `StackedAreaChart` and
 * `StackedBarChart` (horizontal), the last two also as 100 % variants. All are SVG, one keyboard focus stop, a tooltip that
 * follows the arrow keys, a text summary, and `onSelect({ dimension, value })` on click or Enter / Space.
 */
import { useRef, type ReactElement } from 'react';
import {
  bandScale,
  buildSeriesSummary,
  cumulativeCurve,
  dualAxis,
  histogram,
  isFiniteNumber,
  linePath,
  linearScale,
  niceTicks,
  paretoSeries,
  pointPositions,
  nearestIndex,
  roundCoord,
  smoothLinePath,
  stackPercent,
  stackSeries,
  valueDomain,
  type HistogramOptions,
  type PathPoint,
} from '@vello/charts-core';
import {
  AXIS_CHAR_WIDTH,
  ChartToolbar,
  chartState,
  emitSelect,
  formatPercentDefault,
  frameMetrics,
  isChosen,
  pointerPosition,
  truncate,
  type ChartSelectProps,
} from './bi-kit';
import type { ChartCsv, ChartExportProps } from './export';
import { markAttrs } from './mark-state';
import {
  ChartLegend,
  ChartLive,
  ChartTooltip,
  cascade,
  colorOf,
  cx,
  defaultFormatValue,
  distinctLabels,
  distinctTicks,
  edgeAnchoredLabelStride,
  joinSummary,
  labelStride,
  seriesColor,
  seriesDash,
  useActivePoint,
  useElementWidth,
  useMotionState,
} from './kit';
import type { ChartBaseProps, LineSeries } from './line-chart';

const FALLBACK_WIDTH = 480;

/** Series values for the first `count` categories, with gaps as `null`. */
function column(series: LineSeries, count: number): (number | null)[] {
  return Array.from({ length: count }, (_, i) => (isFiniteNumber(series.values[i]) ? (series.values[i] as number) : null));
}

// ── Histogram ──────────────────────────────────────────────────────────────────────────────────────────────────────

export interface HistogramMarker {
  readonly value: number;
  /** e.g. "Median" or "p90" — printed at the line and added to the summary. */
  readonly label: string;
}

export interface HistogramProps extends ChartBaseProps, ChartSelectProps, ChartExportProps {
  /** Raw numbers to bin (the chart bins them with nice edges)… */
  readonly values?: readonly (number | null | undefined)[];
  readonly binOptions?: HistogramOptions;
  /** …or bins already counted by the server. */
  readonly bins?: readonly { readonly from: number; readonly to: number; readonly count: number }[];
  /** Overlay the cumulative curve on a 0 – 100 % axis on the right. */
  readonly cumulative?: boolean;
  /** Names the two series in the legend and tooltip (localized): the counts and the cumulative curve. */
  readonly countLabel?: string;
  readonly cumulativeLabel?: string;
  readonly markers?: readonly HistogramMarker[];
  /** Format a bin edge (default: `formatValue`). */
  readonly formatEdge?: (value: number) => string;
  readonly formatShare?: (fraction: number) => string;
  readonly height?: number;
}

interface BinView {
  readonly from: number;
  readonly to: number;
  readonly count: number;
}

/** Bars of a distribution, with an optional cumulative curve and vertical markers. `onSelect` reports the bin as `from..to`. */
export function Histogram(props: HistogramProps): ReactElement {
  const height = props.height ?? 240;
  const bins = histogramBins(props);
  return chartState(props, height, bins.some((b) => b.count > 0)) ?? <HistogramPlot {...props} bins={bins} height={height} />;
}

function histogramBins(props: HistogramProps): readonly BinView[] {
  if (props.bins !== undefined) return props.bins.filter((b) => isFiniteNumber(b.from) && isFiniteNumber(b.to) && isFiniteNumber(b.count));
  return histogram(props.values ?? [], props.binOptions).bins;
}

function HistogramPlot(props: HistogramProps & { bins: readonly BinView[]; height: number }): ReactElement {
  const bins = props.bins;
  const n = bins.length;
  const format = props.formatValue ?? defaultFormatValue;
  const formatEdge = props.formatEdge ?? format;
  const formatShare = props.formatShare ?? formatPercentDefault;
  const cumulative = props.cumulative ?? false;
  const total = bins.reduce((s, b) => s + b.count, 0);
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, FALLBACK_WIDTH);
  const motion = useMotionState(props.animate);
  const binKey = (i: number): string => `${bins[i]?.from}..${bins[i]?.to}`;
  const active = useActivePoint(n, props.onSelect !== undefined ? (i) => emitSelect(props, binKey(i), props.dimension ?? 'bin') : undefined);

  const domain = distinctTicks(valueDomain(bins.map((b) => b.count), { includeZero: true, tickCount: 4 }), format);
  const tickText = domain.ticks.map(format);
  const rightTicks = [0, 0.25, 0.5, 0.75, 1];
  const f = frameMetrics({ width, height: props.height, tickText, ...(cumulative ? { rightTickText: rightTicks.map(formatShare) } : {}), showAxis: true });
  const y = linearScale(domain.domain, [f.baselineY, f.top]);
  const y2 = linearScale([0, 1], [f.baselineY, f.top]);
  const binW = f.plotWidth / n;
  const e0 = bins[0]?.from ?? 0;
  const eN = bins[n - 1]?.to ?? 1;
  const xOfValue = (v: number): number => f.left + ((v - e0) / (eN - e0 || 1)) * f.plotWidth;
  const stride = labelStride(bins.map((b) => formatEdge(b.from)), binW, 52);
  const curve = cumulative
    ? (() => {
        const running: PathPoint[] = [{ x: f.left, y: y2(0) }];
        let run = 0;
        bins.forEach((b, i) => {
          run += b.count;
          running.push({ x: f.left + (i + 1) * binW, y: y2(total > 0 ? run / total : 0) });
        });
        return running;
      })()
    : [];

  const summary =
    props.ariaLabel ??
    joinSummary([
      props.title,
      buildSeriesSummary(bins.map((b) => ({ label: `${formatEdge(b.from)}–${formatEdge(b.to)}`, value: b.count })), { format }),
      ...(props.markers ?? []).map((m) => `${m.label}: ${formatEdge(m.value)}`),
    ]);
  const csv = (): ChartCsv => {
    const curveFull = cumulativeCurve({ bins: bins.map((b, index) => ({ index, from: b.from, to: b.to, count: b.count, share: total > 0 ? b.count / total : 0 })), total, min: e0, max: eN, step: binW });
    return { header: ['from', 'to', props.countLabel ?? 'count', 'share', props.cumulativeLabel ?? 'cumulative'], rows: bins.map((b, i) => [b.from, b.to, b.count, total > 0 ? b.count / total : 0, curveFull[i]?.share ?? null]) };
  };
  const a = active.index;
  const activeBin = a === null ? undefined : bins[a];
  const cumulativeAt = (i: number): number => (total > 0 ? bins.slice(0, i + 1).reduce((s, b) => s + b.count, 0) / total : 0);

  return (
    <div className={cx('vello-chart', 'vello-chart--histogram', props.className)} data-motion={motion}>
      <ChartToolbar props={props} title={props.title} csv={csv} />
      <div ref={ref} className="vello-chart__plot vello-chart__focus" role="img" aria-label={summary} tabIndex={0} onKeyDown={active.onKeyDown} onFocus={active.onFocus} onBlur={active.onBlur}>
        <svg
          className="vello-chart__svg"
          width={width}
          height={props.height}
          viewBox={`0 0 ${width} ${props.height}`}
          onClick={(e) => active.activateFromPointer(e, (x) => (x >= f.left && x <= f.left + f.plotWidth ? Math.min(n - 1, Math.floor((x - f.left) / binW)) : -1))}
          onPointerMove={(e) => active.fromPointer(e, (x) => (x >= f.left && x <= f.left + f.plotWidth ? Math.min(n - 1, Math.floor((x - f.left) / binW)) : -1))}
          onPointerLeave={active.onPointerLeave}
        >
          {domain.ticks.map((t, i) => (
            <g key={`t${t}`}>
              <line className={t === 0 ? 'vello-chart__baseline' : 'vello-chart__grid'} x1={f.left} x2={f.left + f.plotWidth} y1={y(t)} y2={y(t)} />
              <text className="vello-chart__tick tnum" x={f.left - 8} y={y(t)} dy="0.32em" textAnchor="end">
                {tickText[i]}
              </text>
            </g>
          ))}
          {cumulative &&
            rightTicks.map((t) => (
              <text key={`r${t}`} className="vello-chart__tick tnum" x={f.left + f.plotWidth + 8} y={y2(t)} dy="0.32em">
                {formatShare(t)}
              </text>
            ))}
          {bins.map((b, i) => {
            const h = Math.max(0, f.baselineY - y(b.count));
            return (
              <g key={i} {...markAttrs(props, binKey(i), a === i)}>
                <rect className="vello-chart__bar vello-chart__rise" x={roundCoord(f.left + i * binW + 0.5)} y={roundCoord(f.baselineY - h)} width={roundCoord(Math.max(1, binW - 1))} height={roundCoord(h)} fill={seriesColor(0)} style={cascade(i)} />
              </g>
            );
          })}
          {Array.from({ length: n + 1 }, (_, i) =>
            i % stride === 0 || i === n ? (
              <text key={`e${i}`} className="vello-chart__tick tnum" x={f.left + i * binW} y={f.baselineY + 18} textAnchor="middle">
                {formatEdge(i === n ? (bins[n - 1]?.to ?? 0) : (bins[i]?.from ?? 0))}
              </text>
            ) : null,
          )}
          {cumulative && <path className="vello-chart__line vello-chart__draw" d={linePath(curve)} stroke={seriesColor(1)} strokeDasharray={seriesDash(1)} />}
          {(props.markers ?? []).map((m, i) => {
            const x = Math.min(f.left + f.plotWidth, Math.max(f.left, xOfValue(m.value)));
            return (
              <g key={`m${i}`} className="vello-chart__target">
                <line className="vello-chart__target-halo" x1={x} x2={x} y1={f.top} y2={f.baselineY} />
                <line className="vello-chart__target-line" x1={x} x2={x} y1={f.top} y2={f.baselineY} />
                <text className="vello-chart__target-label" x={x + 4} y={f.top + 10 + i * 12}>
                  {m.label}
                </text>
              </g>
            );
          })}
        </svg>
        {a !== null && activeBin !== undefined && (
          <ChartTooltip
            x={f.left + (a + 0.5) * binW}
            y={y(activeBin.count)}
            width={width}
            heading={`${formatEdge(activeBin.from)}–${formatEdge(activeBin.to)}`}
            rows={[
              { key: 'c', ...(props.countLabel !== undefined ? { label: props.countLabel } : {}), value: `${format(activeBin.count)}${total > 0 ? ` (${formatShare(activeBin.count / total)})` : ''}` },
              ...(cumulative ? [{ key: 'cum', ...(props.cumulativeLabel !== undefined ? { label: props.cumulativeLabel } : {}), value: formatShare(cumulativeAt(a)) }] : []),
            ]}
          />
        )}
      </div>
      <ChartLive text={activeBin === undefined ? '' : `${formatEdge(activeBin.from)}–${formatEdge(activeBin.to)}: ${format(activeBin.count)}`} />
      {cumulative && props.countLabel !== undefined && props.cumulativeLabel !== undefined && (
        <ChartLegend
          items={[
            { key: 'count', label: props.countLabel, color: seriesColor(0), shape: 'bar' },
            { key: 'cum', label: props.cumulativeLabel, color: seriesColor(1), dash: seriesDash(1) },
          ]}
        />
      )}
    </div>
  );
}

// ── Combo (bars + lines, two axes) ─────────────────────────────────────────────────────────────────────────────────

export interface ComboChartProps extends ChartBaseProps, ChartSelectProps, ChartExportProps {
  readonly categories: readonly string[];
  /** Series drawn as columns on the LEFT axis (several are grouped side by side). */
  readonly bars: readonly LineSeries[];
  /** Series drawn as lines on the RIGHT axis. */
  readonly lines: readonly LineSeries[];
  /** The two axes' titles (localized): what each scale measures. They are drawn above the axes. */
  readonly leftAxisLabel?: string;
  readonly rightAxisLabel?: string;
  /** Format the right axis and the line values (default: `formatValue`). */
  readonly formatRight?: (value: number) => string;
  readonly includeZeroRight?: boolean;
  readonly height?: number;
  readonly smooth?: boolean;
}

/** Columns on the left axis and lines on the right axis, with both axes' gridlines aligned. */
export function ComboChart(props: ComboChartProps): ReactElement {
  const height = props.height ?? 260;
  const n = props.categories.length;
  const hasData = n > 0 && [...props.bars, ...props.lines].some((s) => s.values.slice(0, n).some(isFiniteNumber));
  return chartState(props, height, hasData) ?? <ComboChartPlot {...props} height={height} />;
}

function ComboChartPlot(props: ComboChartProps & { height: number }): ReactElement {
  const { categories, bars, lines } = props;
  const n = categories.length;
  const format = props.formatValue ?? defaultFormatValue;
  const formatRight = props.formatRight ?? format;
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, FALLBACK_WIDTH);
  const motion = useMotionState(props.animate);
  const active = useActivePoint(n, props.onSelect !== undefined ? (i) => emitSelect(props, categories[i] ?? '') : undefined);

  const barVals = bars.flatMap((s) => column(s, n));
  const lineVals = lines.flatMap((s) => column(s, n));
  const axes = dualAxis(barVals, lines.length > 0 ? lineVals : barVals, { includeZeroRight: props.includeZeroRight ?? true });
  const leftText = distinctLabels(axes.left.ticks.map(format));
  const rightText = distinctLabels(axes.right.ticks.map(formatRight));
  const hasRight = lines.length > 0;
  const f = frameMetrics({ width, height: props.height, tickText: leftText, ...(hasRight ? { rightTickText: rightText } : {}), showAxis: true, top: 26 });
  const y = linearScale(axes.left.domain, [f.baselineY, f.top]);
  const y2 = linearScale(axes.right.domain, [f.baselineY, f.top]);
  const band = bandScale(n, [f.left, f.left + f.plotWidth], { padding: n > 12 ? 0.2 : 0.35 });
  const centers = Array.from({ length: n }, (_, i) => band.center(i));
  const sub = band.bandwidth / Math.max(1, bars.length);
  const stride = labelStride(categories, band.step, 44);

  const summary =
    props.ariaLabel ??
    joinSummary([
      props.title,
      ...bars.map((s) => buildSeriesSummary(categories.map((label, i) => ({ label, value: s.values[i] })), { title: s.label, format })),
      ...lines.map((s) => buildSeriesSummary(categories.map((label, i) => ({ label, value: s.values[i] })), { title: s.label, format: formatRight })),
    ]);
  const csv = (): ChartCsv => ({ header: ['', ...bars.map((s) => s.label), ...lines.map((s) => s.label)], rows: categories.map((c, i) => [c, ...bars.map((s) => column(s, n)[i] ?? null), ...lines.map((s) => column(s, n)[i] ?? null)]) });
  const a = active.index;
  const rows =
    a === null
      ? []
      : [
          ...bars.map((s, si) => ({ key: s.key, label: s.label, color: colorOf(si + 2, s.tone), value: isFiniteNumber(s.values[a]) ? format(s.values[a] as number) : '–' })),
          ...lines.map((s, si) => ({ key: s.key, label: s.label, color: colorOf(si, s.tone), value: isFiniteNumber(s.values[a]) ? formatRight(s.values[a] as number) : '–' })),
        ];
  const lineY = (v: number | null): number | null => (v === null ? null : y2(v));

  return (
    <div className={cx('vello-chart', 'vello-chart--combo', props.className)} data-motion={motion}>
      <ChartToolbar props={props} title={props.title} csv={csv} />
      <div ref={ref} className="vello-chart__plot vello-chart__focus" role="img" aria-label={summary} tabIndex={0} onKeyDown={active.onKeyDown} onFocus={active.onFocus} onBlur={active.onBlur}>
        <svg
          className="vello-chart__svg"
          width={width}
          height={props.height}
          viewBox={`0 0 ${width} ${props.height}`}
          onClick={(e) => active.activateFromPointer(e, (x) => band.indexAt(x))}
          onPointerMove={(e) => active.fromPointer(e, (x) => band.indexAt(x))}
          onPointerLeave={active.onPointerLeave}
        >
          {props.leftAxisLabel !== undefined && (
            <text className="vello-chart__axis-title" x={f.left - 8} y={12} textAnchor="start">
              {props.leftAxisLabel}
            </text>
          )}
          {hasRight && props.rightAxisLabel !== undefined && (
            <text className="vello-chart__axis-title" x={f.left + f.plotWidth + 8} y={12} textAnchor="end">
              {props.rightAxisLabel}
            </text>
          )}
          {axes.left.ticks.map((t, i) => (
            <g key={`t${i}`}>
              <line className={t === 0 ? 'vello-chart__baseline' : 'vello-chart__grid'} x1={f.left} x2={f.left + f.plotWidth} y1={y(t)} y2={y(t)} />
              <text className="vello-chart__tick tnum" x={f.left - 8} y={y(t)} dy="0.32em" textAnchor="end">
                {leftText[i]}
              </text>
            </g>
          ))}
          {hasRight &&
            axes.right.ticks.map((t, i) => (
              <text key={`r${i}`} className="vello-chart__tick tnum" x={f.left + f.plotWidth + 8} y={y2(t)} dy="0.32em">
                {rightText[i]}
              </text>
            ))}
          {a !== null && <rect className="vello-chart__band" x={band.start(a) - (band.step - band.bandwidth) / 2} y={f.top} width={band.step} height={f.plotHeight} />}
          {bars.map((s, si) =>
            column(s, n).map((v, i) =>
              v === null ? null : (
                <rect
                  key={`${s.key}-${i}`}
                  className="vello-chart__bar vello-chart__rise"
                  x={roundCoord(band.start(i) + si * sub)}
                  y={roundCoord(Math.min(y(v), y(0)))}
                  width={roundCoord(Math.max(1, sub))}
                  height={roundCoord(Math.abs(y(0) - y(v)))}
                  rx={2}
                  fill={colorOf(si + 2, s.tone)}
                  style={cascade(i)}
                  {...markAttrs(props, categories[i] ?? '', a === i)}
                />
              ),
            ),
          )}
          {lines.map((s, si) => {
            const pts: PathPoint[] = column(s, n).map((v, i) => ({ x: centers[i] ?? 0, y: lineY(v) }));
            return (
              <g key={s.key}>
                <path className="vello-chart__line vello-chart__draw" d={props.smooth === false ? linePath(pts) : smoothLinePath(pts)} stroke={colorOf(si, s.tone)} {...(seriesDash(si) !== undefined ? { strokeDasharray: seriesDash(si) } : {})} />
                {pts.map((p, i) => (p.y === null ? null : <circle key={i} className="vello-chart__dot" cx={p.x} cy={p.y} r={a === i ? 4 : 3} fill={colorOf(si, s.tone)} {...markAttrs(props, categories[i] ?? '', a === i)} />))}
              </g>
            );
          })}
          {categories.map((label, i) =>
            i % stride === 0 ? (
              <text key={`x${i}`} className="vello-chart__tick" x={band.center(i)} y={f.baselineY + 18} textAnchor="middle">
                {truncate(label, (band.step * stride) / 6.2)}
              </text>
            ) : null,
          )}
        </svg>
        {a !== null && <ChartTooltip x={band.center(a)} y={f.top} width={width} heading={categories[a] ?? ''} rows={rows} />}
      </div>
      <ChartLive text={a === null ? '' : `${categories[a] ?? ''}: ${rows.map((r) => `${r.label} ${r.value}`).join(', ')}`} />
      <ChartLegend
        items={[
          ...bars.map((s, si) => ({ key: s.key, label: s.label, color: colorOf(si + 2, s.tone), shape: 'bar' as const })),
          ...lines.map((s, si) => ({ key: s.key, label: s.label, color: colorOf(si, s.tone), dash: seriesDash(si) })),
        ]}
      />
    </div>
  );
}

// ── Pareto ─────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface ParetoChartProps extends ChartBaseProps, ChartSelectProps, ChartExportProps {
  readonly items: readonly { readonly key: string; readonly label: string; readonly value: number | null | undefined }[];
  /** Where class A and class B end, as cumulative shares (default 0.8 and 0.95). */
  readonly thresholds?: readonly [number, number];
  /** The name of a class (e.g. `Class A`); the letter alone when omitted. Printed in the legend and the summary. */
  readonly classLabel?: (cls: 'A' | 'B' | 'C') => string;
  /** The name of the cumulative line (localized). */
  readonly cumulativeLabel?: string;
  readonly formatShare?: (fraction: number) => string;
  readonly height?: number;
}

const CLASS_COLOR_INDEX = { A: 0, B: 2, C: 3 } as const;

/** Value bars sorted high to low, the cumulative share as a line on a 0 – 100 % axis, and the A / B / C class of every item (also written under the bar). */
export function ParetoChart(props: ParetoChartProps): ReactElement {
  const height = props.height ?? 260;
  return chartState(props, height, props.items.some((i) => isFiniteNumber(i.value) && i.value > 0)) ?? <ParetoPlot {...props} height={height} />;
}

function ParetoPlot(props: ParetoChartProps & { height: number }): ReactElement {
  const pareto = paretoSeries(props.items, props.thresholds !== undefined ? { thresholds: props.thresholds } : {});
  const items = pareto.items;
  const n = items.length;
  const thresholds = props.thresholds ?? ([0.8, 0.95] as const);
  const format = props.formatValue ?? defaultFormatValue;
  const formatShare = props.formatShare ?? formatPercentDefault;
  const classLabel = props.classLabel ?? ((c: 'A' | 'B' | 'C') => c);
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, FALLBACK_WIDTH);
  const motion = useMotionState(props.animate);
  const active = useActivePoint(n, props.onSelect !== undefined ? (i) => emitSelect(props, items[i]?.key ?? '', props.dimension ?? 'item') : undefined);

  const domain = distinctTicks(valueDomain(items.map((i) => i.value), { includeZero: true, tickCount: 4 }), format);
  const tickText = domain.ticks.map(format);
  const rightTicks = [0, 0.25, 0.5, 0.75, 1];
  const f = frameMetrics({ width, height: props.height, tickText, rightTickText: rightTicks.map(formatShare), showAxis: true, xAxisHeight: 44 });
  const y = linearScale(domain.domain, [f.baselineY, f.top]);
  const y2 = linearScale([0, 1], [f.baselineY, f.top]);
  const band = bandScale(n, [f.left, f.left + f.plotWidth], { padding: n > 12 ? 0.2 : 0.3 });
  const stride = labelStride(items.map((it) => it.label), band.step, 44);
  const curve: PathPoint[] = items.map((it, i) => ({ x: band.center(i), y: y2(it.cumulative) }));

  const summary =
    props.ariaLabel ??
    joinSummary([
      props.title,
      items.map((i) => `${i.label}: ${format(i.value)} (${formatShare(i.cumulative)}, ${classLabel(i.cls)})`).join('; '),
    ]);
  const csv = (): ChartCsv => ({ header: ['', 'value', 'share', props.cumulativeLabel ?? 'cumulative', 'class'], rows: items.map((i) => [i.label, i.value, i.share, i.cumulative, i.cls]) });
  const a = active.index;
  const activeItem = a === null ? undefined : items[a];

  return (
    <div className={cx('vello-chart', 'vello-chart--pareto', props.className)} data-motion={motion}>
      <ChartToolbar props={props} title={props.title} csv={csv} />
      <div ref={ref} className="vello-chart__plot vello-chart__focus" role="img" aria-label={summary} tabIndex={0} onKeyDown={active.onKeyDown} onFocus={active.onFocus} onBlur={active.onBlur}>
        <svg
          className="vello-chart__svg"
          width={width}
          height={props.height}
          viewBox={`0 0 ${width} ${props.height}`}
          onClick={(e) => active.activateFromPointer(e, (x) => band.indexAt(x))}
          onPointerMove={(e) => active.fromPointer(e, (x) => band.indexAt(x))}
          onPointerLeave={active.onPointerLeave}
        >
          {domain.ticks.map((t, i) => (
            <g key={`t${t}`}>
              <line className={t === 0 ? 'vello-chart__baseline' : 'vello-chart__grid'} x1={f.left} x2={f.left + f.plotWidth} y1={y(t)} y2={y(t)} />
              <text className="vello-chart__tick tnum" x={f.left - 8} y={y(t)} dy="0.32em" textAnchor="end">
                {tickText[i]}
              </text>
            </g>
          ))}
          {rightTicks.map((t) => (
            <text key={`r${t}`} className="vello-chart__tick tnum" x={f.left + f.plotWidth + 8} y={y2(t)} dy="0.32em">
              {formatShare(t)}
            </text>
          ))}
          {thresholds.map((t, i) => (
            <line key={`th${i}`} className="vello-chart__crosshair" x1={f.left} x2={f.left + f.plotWidth} y1={y2(t)} y2={y2(t)} />
          ))}
          {a !== null && <rect className="vello-chart__band" x={band.start(a) - (band.step - band.bandwidth) / 2} y={f.top} width={band.step} height={f.plotHeight} />}
          {items.map((it, i) => (
            <rect
              key={it.key}
              className="vello-chart__bar vello-chart__rise"
              x={roundCoord(band.start(i))}
              y={roundCoord(y(it.value))}
              width={roundCoord(band.bandwidth)}
              height={roundCoord(Math.max(0, f.baselineY - y(it.value)))}
              rx={2}
              fill={seriesColor(CLASS_COLOR_INDEX[it.cls])}
              style={cascade(i)}
              {...markAttrs(props, it.key, a === i)}
            />
          ))}
          <path className="vello-chart__line vello-chart__draw" d={linePath(curve)} stroke={seriesColor(1)} strokeDasharray={seriesDash(1)} />
          {curve.map((p, i) => (
            <circle key={i} className="vello-chart__dot" cx={p.x} cy={p.y ?? 0} r={a === i ? 4 : 2.5} fill={seriesColor(1)} {...markAttrs(props, items[i]?.key ?? '', a === i)} />
          ))}
          {items.map((it, i) =>
            i % stride === 0 ? (
              <g key={`x${i}`}>
                <text className="vello-chart__tick" x={band.center(i)} y={f.baselineY + 16} textAnchor="middle">
                  {truncate(it.label, (band.step * stride) / 6.2)}
                </text>
                <text className="vello-chart__class-mark" x={band.center(i)} y={f.baselineY + 34} textAnchor="middle">
                  {it.cls}
                </text>
              </g>
            ) : null,
          )}
        </svg>
        {a !== null && activeItem !== undefined && (
          <ChartTooltip
            x={band.center(a)}
            y={y(activeItem.value)}
            width={width}
            heading={activeItem.label}
            rows={[
              { key: 'v', value: `${format(activeItem.value)} (${formatShare(activeItem.share)})` },
              { key: 'c', ...(props.cumulativeLabel !== undefined ? { label: props.cumulativeLabel } : {}), value: formatShare(activeItem.cumulative) },
              { key: 'k', value: classLabel(activeItem.cls) },
            ]}
          />
        )}
      </div>
      <ChartLive text={activeItem === undefined ? '' : `${activeItem.label}: ${format(activeItem.value)}, ${formatShare(activeItem.cumulative)}, ${classLabel(activeItem.cls)}`} />
      <ChartLegend
        items={[
          ...(['A', 'B', 'C'] as const).map((c) => ({ key: c, label: `${c} · ${classLabel(c)}`, color: seriesColor(CLASS_COLOR_INDEX[c]), shape: 'bar' as const })),
          ...(props.cumulativeLabel !== undefined ? [{ key: 'cum', label: props.cumulativeLabel, color: seriesColor(1), dash: seriesDash(1) }] : []),
        ]}
      />
    </div>
  );
}

// ── Stacked area ───────────────────────────────────────────────────────────────────────────────────────────────────

export interface StackedAreaChartProps extends ChartBaseProps, ChartSelectProps, ChartExportProps {
  readonly labels: readonly string[];
  readonly series: readonly LineSeries[];
  /** Stack to 100 %: every x shows the share of each series. */
  readonly percent?: boolean;
  readonly formatShare?: (fraction: number) => string;
  /** Name of the total row in the tooltip (omit to hide). */
  readonly totalLabel?: string;
  readonly height?: number;
  readonly showGrid?: boolean;
  readonly yTicks?: number;
}

/** Series stacked as bands over time (non-negative data; a negative value counts as 0), optionally to 100 %. */
export function StackedAreaChart(props: StackedAreaChartProps): ReactElement {
  const height = props.height ?? 240;
  const n = props.labels.length;
  return chartState(props, height, n > 0 && props.series.some((s) => s.values.slice(0, n).some(isFiniteNumber))) ?? <StackedAreaPlot {...props} height={height} />;
}

function StackedAreaPlot(props: StackedAreaChartProps & { height: number }): ReactElement {
  const { labels, series } = props;
  const n = labels.length;
  const percent = props.percent ?? false;
  const format = props.formatValue ?? defaultFormatValue;
  const formatShare = props.formatShare ?? formatPercentDefault;
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, FALLBACK_WIDTH);
  const motion = useMotionState(props.animate);
  const active = useActivePoint(n, props.onSelect !== undefined ? (i) => emitSelect(props, labels[i] ?? '') : undefined);

  const inputs = series.map((s) => ({ key: s.key, values: column(s, n).map((v) => (v === null ? null : Math.max(0, v))) }));
  const layers = percent ? stackPercent(inputs, n).layers : stackSeries(inputs, n);
  const totals = Array.from({ length: n }, (_, i) => inputs.reduce((sum, s) => sum + (s.values[i] ?? 0), 0));
  const top = percent ? 1 : Math.max(0, ...totals);
  const domain = distinctTicks(percent ? niceTicks(0, 1, props.yTicks ?? 4) : valueDomain([0, top], { includeZero: true, tickCount: props.yTicks ?? 4 }), (t) => (percent ? formatShare(t) : format(t)));
  const tickText = domain.ticks.map((t) => (percent ? formatShare(t) : format(t)));
  const f = frameMetrics({ width, height: props.height, tickText, showAxis: true });
  const y = linearScale(domain.domain, [f.baselineY, f.top]);
  const xs = pointPositions(n, [f.left, f.left + f.plotWidth]);
  const stride = edgeAnchoredLabelStride(labels, n > 1 ? f.plotWidth / (n - 1) : f.plotWidth, 64);

  const areaD = (li: number): string => {
    const layer = layers[li];
    if (layer === undefined || n === 0) return '';
    const upper = layer.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${roundCoord(xs[i] ?? 0)} ${roundCoord(y(p.y1))}`).join('');
    const lower = [...layer.points].map((p, i) => ({ p, i })).reverse().map(({ p, i }) => `L${roundCoord(xs[i] ?? 0)} ${roundCoord(y(p.y0))}`).join('');
    return `${upper}${lower}Z`;
  };

  const summary =
    props.ariaLabel ??
    joinSummary([
      props.title,
      ...series.map((s) => buildSeriesSummary(labels.map((label, i) => ({ label, value: s.values[i] })), { title: s.label, format })),
    ]);
  const csv = (): ChartCsv => ({ header: ['', ...series.map((s) => s.label)], rows: labels.map((l, i) => [l, ...series.map((s) => column(s, n)[i] ?? null)]) });
  const a = active.index;
  const rows =
    a === null
      ? []
      : [
          ...series.map((s, si) => ({
            key: s.key,
            label: s.label,
            color: colorOf(si, s.tone),
            value: isFiniteNumber(s.values[a]) ? `${format(s.values[a] as number)}${percent && (totals[a] ?? 0) > 0 ? ` (${formatShare(Math.max(0, s.values[a] as number) / (totals[a] ?? 1))})` : ''}` : '–',
          })),
          ...(props.totalLabel !== undefined ? [{ key: '__total', label: props.totalLabel, color: undefined, value: format(totals[a] ?? 0) }] : []),
        ];

  return (
    <div className={cx('vello-chart', 'vello-chart--stacked-area', props.className)} data-motion={motion}>
      <ChartToolbar props={props} title={props.title} csv={csv} />
      <div ref={ref} className="vello-chart__plot vello-chart__focus" role="img" aria-label={summary} tabIndex={0} onKeyDown={active.onKeyDown} onFocus={active.onFocus} onBlur={active.onBlur}>
        <svg
          className="vello-chart__svg"
          width={width}
          height={props.height}
          viewBox={`0 0 ${width} ${props.height}`}
          onClick={(e) => active.activateFromPointer(e, (x) => nearestIndex(xs, x))}
          onPointerMove={(e) => active.fromPointer(e, (x) => nearestIndex(xs, x))}
          onPointerLeave={active.onPointerLeave}
        >
          {(props.showGrid ?? true) && domain.ticks.map((t) => <line key={`g${t}`} className={t === 0 ? 'vello-chart__baseline' : 'vello-chart__grid'} x1={f.left} x2={f.left + f.plotWidth} y1={y(t)} y2={y(t)} />)}
          {domain.ticks.map((t, i) => (
            <text key={`t${t}`} className="vello-chart__tick tnum" x={f.left - 8} y={y(t)} dy="0.32em" textAnchor="end">
              {tickText[i]}
            </text>
          ))}
          {labels.map((label, i) =>
            i % stride === 0 ? (
              <text key={`x${i}`} className="vello-chart__tick" x={xs[i]} y={f.baselineY + 18} textAnchor={i === 0 && n > 1 ? 'start' : i === n - 1 && n > 1 ? 'end' : 'middle'}>
                {label}
              </text>
            ) : null,
          )}
          {layers.map((layer, li) => (
            <path key={layer.key} className="vello-chart__stack-area vello-chart__fade" d={areaD(li)} fill={colorOf(li, series[li]?.tone)} />
          ))}
          {labels.map((label, i) =>
            isChosen(props, label) && xs[i] !== undefined ? <line key={`c${i}`} className="vello-chart__chosen-line" data-chosen="true" x1={xs[i]} x2={xs[i]} y1={f.top} y2={f.baselineY} /> : null,
          )}
          {a !== null && <line className="vello-chart__crosshair" x1={xs[a]} x2={xs[a]} y1={f.top} y2={f.baselineY} />}
          {a !== null &&
            layers.map((layer, li) => {
              const p = layer.points[a];
              return p === undefined || p.value <= 0 || xs[a] === undefined ? null : (
                <circle key={layer.key} className="vello-chart__layer-dot" cx={xs[a]} cy={roundCoord(y(p.y1))} r={4} fill={colorOf(li, series[li]?.tone)} />
              );
            })}
        </svg>
        {a !== null && xs[a] !== undefined && <ChartTooltip x={xs[a] as number} y={y(percent ? 1 : (totals[a] ?? 0))} width={width} heading={labels[a] ?? ''} rows={rows} />}
      </div>
      <ChartLive text={a === null ? '' : `${labels[a] ?? ''}: ${rows.map((r) => `${r.label} ${r.value}`).join(', ')}`} />
      <ChartLegend items={series.map((s, si) => ({ key: s.key, label: s.label, color: colorOf(si, s.tone), shape: 'bar' as const }))} />
    </div>
  );
}

// ── Stacked horizontal bars ────────────────────────────────────────────────────────────────────────────────────────

export interface StackedBarChartProps extends ChartBaseProps, ChartSelectProps, ChartExportProps {
  /** One row per category. */
  readonly categories: readonly string[];
  /** The segments (one series per segment kind). */
  readonly series: readonly LineSeries[];
  readonly percent?: boolean;
  readonly formatShare?: (fraction: number) => string;
  readonly totalLabel?: string;
  /** Height of one row (default 36). */
  readonly rowHeight?: number;
}

const BAR_AXIS = 24;

/** Horizontal stacked bars (or 100 % bars): a row per category, a segment per series; the row total is printed at the end. */
export function StackedBarChart(props: StackedBarChartProps): ReactElement {
  const rows = props.categories.length;
  const height = Math.max(1, rows) * (props.rowHeight ?? 36) + BAR_AXIS;
  return chartState(props, height, rows > 0 && props.series.some((s) => s.values.slice(0, rows).some(isFiniteNumber))) ?? <StackedBarPlot {...props} />;
}

function StackedBarPlot(props: StackedBarChartProps): ReactElement {
  const { categories, series } = props;
  const n = categories.length;
  const rowH = props.rowHeight ?? 36;
  const percent = props.percent ?? false;
  const format = props.formatValue ?? defaultFormatValue;
  const formatShare = props.formatShare ?? formatPercentDefault;
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, FALLBACK_WIDTH);
  const motion = useMotionState(props.animate);
  const active = useActivePoint(n, props.onSelect !== undefined ? (i) => emitSelect(props, categories[i] ?? '') : undefined);

  const inputs = series.map((s) => ({ key: s.key, values: column(s, n).map((v) => (v === null ? null : Math.max(0, v))) }));
  const layers = percent ? stackPercent(inputs, n).layers : stackSeries(inputs, n);
  const totals = Array.from({ length: n }, (_, i) => inputs.reduce((sum, s) => sum + (s.values[i] ?? 0), 0));
  const domain = distinctTicks(percent ? niceTicks(0, 1, 4) : valueDomain([0, ...totals], { includeZero: true, tickCount: 4 }), (t) => (percent ? formatShare(t) : format(t)));
  const totalText = totals.map((t) => (percent ? '' : format(t)));
  const labelChars = categories.reduce((m, c) => Math.max(m, Math.min(c.length, 22)), 0);
  const left = clampNum(labelChars * AXIS_CHAR_WIDTH + 14, 40, 150);
  const rightPad = percent ? 8 : clampNum(totalText.reduce((m, t) => Math.max(m, t.length), 0) * AXIS_CHAR_WIDTH + 12, 8, 90);
  const plotWidth = Math.max(40, width - left - rightPad);
  const height = n * rowH + BAR_AXIS;
  const x = linearScale(domain.domain, [left, left + plotWidth]);
  const indexAtY = (py: number): number => Math.min(n - 1, Math.max(0, Math.floor(py / rowH)));

  const summary =
    props.ariaLabel ??
    joinSummary([
      props.title,
      categories
        .map((c, i) => `${c}: ${series.map((s) => `${s.label} ${isFiniteNumber(s.values[i]) ? format(s.values[i] as number) : '–'}`).join(', ')}`)
        .join('; '),
    ]);
  const csv = (): ChartCsv => ({ header: ['', ...series.map((s) => s.label)], rows: categories.map((c, i) => [c, ...series.map((s) => column(s, n)[i] ?? null)]) });
  const a = active.index;
  const rows =
    a === null
      ? []
      : [
          ...series.map((s, si) => ({
            key: s.key,
            label: s.label,
            color: colorOf(si, s.tone),
            value: isFiniteNumber(s.values[a]) ? `${format(s.values[a] as number)}${percent && (totals[a] ?? 0) > 0 ? ` (${formatShare(Math.max(0, s.values[a] as number) / (totals[a] ?? 1))})` : ''}` : '–',
          })),
          ...(props.totalLabel !== undefined ? [{ key: '__total', label: props.totalLabel, color: undefined, value: format(totals[a] ?? 0) }] : []),
        ];

  return (
    <div className={cx('vello-chart', 'vello-chart--stacked-bars', props.className)} data-motion={motion}>
      <ChartToolbar props={props} title={props.title} csv={csv} />
      <div ref={ref} className="vello-chart__plot vello-chart__focus" role="img" aria-label={summary} tabIndex={0} onKeyDown={active.onKeyDown} onFocus={active.onFocus} onBlur={active.onBlur}>
        <svg
          className="vello-chart__svg"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          onClick={(e) => {
            if (props.onSelect !== undefined) emitSelect(props, categories[indexAtY(pointerPosition(e).y)] ?? '');
          }}
          onPointerMove={(e) => active.set(indexAtY(pointerPosition(e).y))}
          onPointerLeave={active.onPointerLeave}
        >
          {domain.ticks.map((t) => (
            <g key={`g${t}`}>
              <line className={t === 0 ? 'vello-chart__baseline' : 'vello-chart__grid'} x1={x(t)} x2={x(t)} y1={0} y2={n * rowH} />
              <text className="vello-chart__tick tnum" x={x(t)} y={n * rowH + 16} textAnchor="middle">
                {percent ? formatShare(t) : format(t)}
              </text>
            </g>
          ))}
          {categories.map((c, i) => (
            <g key={c + i} {...markAttrs(props, c, a === i)}>
              {a === i && <rect className="vello-chart__band" x={0} y={i * rowH} width={width} height={rowH} />}
              <text className="vello-chart__tick" x={left - 8} y={i * rowH + rowH / 2} dy="0.32em" textAnchor="end">
                {truncate(c, 22)}
              </text>
              {layers.map((layer, li) => {
                const p = layer.points[i];
                if (p === undefined || p.value <= 0) return null;
                return (
                  <rect
                    key={layer.key}
                    className="vello-chart__bar vello-chart__extend"
                    x={roundCoord(x(p.y0))}
                    y={i * rowH + 6}
                    width={roundCoord(Math.max(1, x(p.y1) - x(p.y0)))}
                    height={rowH - 12}
                    fill={colorOf(li, series[li]?.tone)}
                    style={cascade(i)}
                  />
                );
              })}
              {!percent && (
                <text className="vello-chart__value tnum" x={x(totals[i] ?? 0) + 6} y={i * rowH + rowH / 2} dy="0.32em">
                  {totalText[i]}
                </text>
              )}
            </g>
          ))}
        </svg>
        {a !== null && <ChartTooltip x={Math.min(width - 8, x(totals[a] ?? 0))} y={a * rowH} width={width} heading={categories[a] ?? ''} rows={rows} />}
      </div>
      <ChartLive text={a === null ? '' : `${categories[a] ?? ''}: ${rows.map((r) => `${r.label} ${r.value}`).join(', ')}`} />
      <ChartLegend items={series.map((s, si) => ({ key: s.key, label: s.label, color: colorOf(si, s.tone), shape: 'bar' as const }))} />
    </div>
  );
}

function clampNum(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
